import { useRouter } from "next/router"
import { useMutation, useQuery } from "@blitzjs/rpc"
import { Routes } from "@blitzjs/next"
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import Card from "@mui/material/Card"
import Button from "@mui/material/Button"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogActions from "@mui/material/DialogActions"
import Alert from "@mui/material/Alert"
import UploadIcon from "@mui/icons-material/Upload"
import EditIcon from "@mui/icons-material/Edit"

import { PipelineType, UniquePageviewsConditions } from "pages/pipelines"
import deployPipelines from "app/pipelines/mutations/deployPipelines"
import usePipelineBulkActions from "app/pipelines/hooks/usePipelineBulkActions"
import getPipelines from "app/pipelines/queries/getPipelines"
import useBulkDeployPipelines from "../hooks/useBulkDeployPipelines"
import PipelineDeployList from "./PipelineDeployList"

export const PipelineBulkActionsContext = createContext<{
  selectedPipelines: PipelineType[]
  setSelectedPipelines: Dispatch<SetStateAction<PipelineType[]>>
  clearSelectedPipelines: () => void
  toggleSelectPipeline: (pipeline: PipelineType) => void
}>({
  selectedPipelines: [],
  setSelectedPipelines: () => {},
  clearSelectedPipelines: () => {},
  toggleSelectPipeline: () => {},
})

const PipelineBulkActionsProvider = ({ children }) => {
  const [selectedPipelines, setSelectedPipelines] = useState<PipelineType[]>([])

  const toggleSelectPipeline = useCallback(
    (pipeline: PipelineType) =>
      setSelectedPipelines((prev) => {
        if (!!prev.find((p) => p.id === pipeline.id)) {
          prev.splice(
            prev.findIndex((p) => p.id === pipeline.id),
            1
          )
          return [...prev]
        } else {
          prev.push(pipeline)
          return [...prev]
        }
      }),
    []
  )

  const clearSelectedPipelines = useCallback(() => setSelectedPipelines([]), [])

  const value = useMemo(
    () => ({
      selectedPipelines,
      setSelectedPipelines,
      clearSelectedPipelines,
      toggleSelectPipeline,
    }),
    [selectedPipelines, clearSelectedPipelines, toggleSelectPipeline]
  )

  return (
    <PipelineBulkActionsContext.Provider value={value}>
      {children}
    </PipelineBulkActionsContext.Provider>
  )
}

interface PipelineBulkActionsProps {
  search: Object
}

export const PipelineBulkActions = ({ search }: PipelineBulkActionsProps) => {
  const { selectedPipelines, setSelectedPipelines, clearSelectedPipelines } =
    usePipelineBulkActions()
  const [isBulkEditEnabled, setBulkEditEnabled] = useState(false)
  const [isSameBrandAlertVisible, setSameBrandAlertVisible] = useState(false)
  const [deployPipelinesMutation, { isLoading: deployPipelinesMutationIsLoading }] =
    useMutation(deployPipelines)
  const [deployPipelinesDialogOpen, setDeployPipelinesDialogOpen] = useState(false)
  const router = useRouter()

  const [getAllPipelinesEnabled, setGetAllPipelinesEnabled] = useState(false)
  const [_, { isFetching }] = useQuery(
    getPipelines,
    {
      where: search,
    },
    {
      suspense: false,
      enabled: getAllPipelinesEnabled,
      onSuccess: ({ pipelines }) => {
        setSelectedPipelines(pipelines)
        setGetAllPipelinesEnabled(false)
      },
    }
  )

  const selectedPipelineIds = selectedPipelines.map((pipeline) => pipeline.id)

  const { errorPipeIds, validPipelineIds, diffPipeIds, diffKeys, samePipeIds } =
    useBulkDeployPipelines(selectedPipelineIds, deployPipelinesDialogOpen)

  useEffect(() => {
    if (!selectedPipelines || !selectedPipelines.length) {
      setBulkEditEnabled(false)
      setSameBrandAlertVisible(false)
      return
    }

    const isSameBrand = selectedPipelines.every(
      (pipeline) => pipeline.brandId === selectedPipelines[0]!.brandId
    )

    setBulkEditEnabled(isSameBrand)
    setSameBrandAlertVisible(!isSameBrand)
  }, [selectedPipelines])

  async function selectAllPipelines() {
    setGetAllPipelinesEnabled(true)
  }

  async function openBulkEditPage() {
    sessionStorage.setItem("pipelineIds", JSON.stringify(selectedPipelineIds))

    await router.push(Routes.PipelinesBulkEditPage())
  }

  return (
    <Card sx={{ mb: 4, p: 2 }}>
      <Grid container spacing={2} direction="row" alignItems="center">
        <Grid item>
          <Typography variant="body2">
            {`${selectedPipelines.length} pipelines selected`}
          </Typography>
        </Grid>
        <Grid item>
          <Button size="small" onClick={selectAllPipelines}>
            {isFetching ? "Loading..." : "Select All"}
          </Button>
          <Button size="small" onClick={clearSelectedPipelines}>
            Clear
          </Button>
        </Grid>

        <Grid item marginLeft="auto"></Grid>

        <Grid item>
          {isSameBrandAlertVisible && (
            <Alert severity="info">
              All selected pipelines should have the same brand in order to bulk edit!
            </Alert>
          )}
        </Grid>

        <Grid item>
          <Tooltip title="Deploy selected pipelines">
            <span>
              <IconButton
                disabled={!selectedPipelines.length}
                onClick={() => setDeployPipelinesDialogOpen(true)}
              >
                <UploadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Edit selected pipelines">
            <span>
              <IconButton
                disabled={!selectedPipelines.length || !isBulkEditEnabled}
                onClick={openBulkEditPage}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <Dialog
        open={deployPipelinesDialogOpen}
        onClose={() => setDeployPipelinesDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`Are you sure you want to deploy ${validPipelineIds.length} pipelines?`}
        </DialogTitle>
        <PipelineDeployList
          selectedPipelines={selectedPipelines}
          pipelineIds={errorPipeIds}
          error
        />
        <PipelineDeployList
          selectedPipelines={selectedPipelines}
          pipelineIds={diffPipeIds}
          diffKeys={diffKeys}
          alert
        />
        <PipelineDeployList selectedPipelines={selectedPipelines} pipelineIds={samePipeIds} />
        <DialogActions>
          <Button onClick={() => setDeployPipelinesDialogOpen(false)}>Close</Button>
          <Button
            disabled={deployPipelinesMutationIsLoading || !validPipelineIds.length}
            onClick={async (e) => {
              try {
                await deployPipelinesMutation({
                  ids: validPipelineIds.map((obj) => obj.pipelineId),
                })
                setDeployPipelinesDialogOpen(false)
              } catch (err) {
                window.alert(err)
              }
            }}
            color="primary"
            variant="contained"
            autoFocus
          >
            Deploy
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default PipelineBulkActionsProvider
